import { useEffect, useRef, useState } from 'react';
import {
  Box, Button, Typography, CircularProgress, Chip, Alert, Dialog, DialogTitle,
  DialogContent, DialogContentText, DialogActions, Stack, Tooltip,
} from '@mui/material';
import { CloudUpload, Image as ImageIcon, BrokenImage, DeleteOutline, SwapHoriz } from '@mui/icons-material';
import {
  uploadSettingFile, removeSettingFile,
  type SettingUploadRules, type StoredFileInfo,
} from '../../services/platform/settingsService';
import { resolveAssetUrl } from '../../utils/assetUrl';

type Props = {
  category: string;
  settingKey: string;
  label: string;
  description?: string;
  value: string;
  accept?: string;
  rules?: SettingUploadRules;
  fileInfo?: StoredFileInfo | null;
  storageMode?: 'local' | 's3';
  onChange: (url: string) => void;
  onUploaded?: (storage: 'local' | 's3') => void;
  onRemoved?: () => void;
};

const FORMAT_LABEL: Record<string, string> = { png: 'PNG', jpeg: 'JPG', webp: 'WEBP', gif: 'GIF', svg: 'SVG', ico: 'ICO' };
const FORMAT_MIME: Record<string, string[]> = {
  png: ['image/png'],
  jpeg: ['image/jpeg', 'image/jpg'],
  webp: ['image/webp'],
  gif: ['image/gif'],
  svg: ['image/svg+xml'],
  ico: ['image/x-icon', 'image/vnd.microsoft.icon'],
};
const FORMAT_EXT: Record<string, string[]> = {
  png: ['png'], jpeg: ['jpg', 'jpeg'], webp: ['webp'], gif: ['gif'], svg: ['svg'], ico: ['ico'],
};

function formatBytes(n?: number) {
  if (!n && n !== 0) return '';
  return n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;
}

function rulesSummary(r: SettingUploadRules) {
  const parts = [
    r.formats.map((f) => FORMAT_LABEL[f] || f.toUpperCase()).join(', '),
    `max ${formatBytes(r.maxBytes)}`,
  ];
  if (r.minWidth || r.minHeight) parts.push(`min ${r.minWidth}×${r.minHeight} px`);
  if (r.maxWidth || r.maxHeight) parts.push(`max ${r.maxWidth}×${r.maxHeight} px`);
  if (r.square) parts.push('square');
  return parts.join(' · ');
}

function readDimensions(file: File): Promise<{ width: number; height: number } | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { resolve({ width: img.naturalWidth, height: img.naturalHeight }); URL.revokeObjectURL(url); };
    img.onerror = () => { resolve(null); URL.revokeObjectURL(url); };
    img.src = url;
  });
}

/** Same checks the server runs, so obvious problems are caught before uploading. */
async function precheck(file: File, label: string, rules?: SettingUploadRules): Promise<string | null> {
  if (!rules) return file.type.startsWith('image/') ? null : 'Only image files are allowed';
  const ext = file.name.split('.').pop()?.toLowerCase() || '';
  const format = rules.formats.find((f) => FORMAT_MIME[f]?.includes(file.type) || FORMAT_EXT[f]?.includes(ext));
  const allowed = rules.formats.map((f) => FORMAT_LABEL[f] || f).join(', ');
  if (!format) return `${label} must be ${allowed}`;
  if (file.size > rules.maxBytes) return `${label} is ${formatBytes(file.size)} — the limit is ${formatBytes(rules.maxBytes)}`;
  if (format === 'ico') return null;
  const dims = await readDimensions(file);
  if (!dims) return `${label} could not be read — the file is corrupted or not a real image`;
  const { width, height } = dims;
  if (format === 'svg' && (!width || !height)) return null;
  if ((rules.minWidth && width < rules.minWidth) || (rules.minHeight && height < rules.minHeight)) {
    return `${label} is ${width}×${height} px — minimum is ${rules.minWidth}×${rules.minHeight} px`;
  }
  if ((rules.maxWidth && width > rules.maxWidth) || (rules.maxHeight && height > rules.maxHeight)) {
    return `${label} is ${width}×${height} px — maximum is ${rules.maxWidth}×${rules.maxHeight} px`;
  }
  if (rules.square && width !== height) return `${label} must be square (this file is ${width}×${height} px)`;
  return null;
}

export default function SettingFileUpload({
  category,
  settingKey,
  label,
  description,
  value,
  accept = 'image/png,image/jpeg,image/webp,image/svg+xml,image/x-icon',
  rules,
  fileInfo,
  storageMode,
  onChange,
  onUploaded,
  onRemoved,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState<'upload' | 'remove' | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [loadFailed, setLoadFailed] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [uploadedInfo, setUploadedInfo] = useState<StoredFileInfo | null>(null);

  const previewUrl = resolveAssetUrl(value);
  useEffect(() => { setLoadFailed(false); }, [previewUrl]);
  useEffect(() => { setUploadedInfo(null); }, [fileInfo]);

  const info = uploadedInfo || fileInfo || null;
  const problem = info?.problem || (loadFailed && previewUrl ? 'The image could not be loaded from its URL — upload it again' : '');

  const handleFile = async (file?: File) => {
    if (inputRef.current) inputRef.current.value = '';
    if (!file) return;
    setError('');
    setNotice('');
    const reason = await precheck(file, label, rules);
    if (reason) {
      setError(reason);
      return;
    }
    setBusy('upload');
    try {
      const result = await uploadSettingFile(file, category, settingKey);
      setUploadedInfo({
        storage: result.storage,
        exists: true,
        fileName: result.fileName,
        bytes: result.bytes,
        width: result.width,
        height: result.height,
        format: result.format,
      });
      onChange(result.url);
      setNotice(result.replaced ? `${label} replaced — the previous file was removed` : `${label} uploaded`);
      onUploaded?.(result.storage);
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        || 'Upload failed';
      setError(message);
    } finally {
      setBusy(null);
    }
  };

  const handleRemove = async () => {
    setConfirmRemove(false);
    setError('');
    setBusy('remove');
    try {
      await removeSettingFile(category, settingKey);
      setUploadedInfo(null);
      onChange('');
      setNotice(`${label} removed`);
      onRemoved?.();
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        || 'Remove failed';
      setError(message);
    } finally {
      setBusy(null);
    }
  };

  const storageLabel = (s?: string) => (s === 's3' ? 'S3' : s === 'external' ? 'External URL' : 'Local server');
  const showPreview = previewUrl && !loadFailed && !info?.problem;

  return (
    <Box>
      <Typography variant="body2" fontWeight={600} gutterBottom>{label}</Typography>
      {description && (
        <Typography variant="caption" color="text.secondary" display="block" mb={1}>
          {description}
        </Typography>
      )}

      <Box display="flex" alignItems="flex-start" gap={2} flexWrap="wrap">
        <Box
          sx={{
            width: 120, height: 80, borderRadius: 2, border: 1,
            borderColor: problem ? 'error.main' : 'divider',
            display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
            // checkerboard so transparent logos stay visible
            backgroundColor: '#fafafa',
            backgroundImage: showPreview
              ? 'linear-gradient(45deg,#eee 25%,transparent 25%),linear-gradient(-45deg,#eee 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#eee 75%),linear-gradient(-45deg,transparent 75%,#eee 75%)'
              : 'none',
            backgroundSize: '16px 16px',
            backgroundPosition: '0 0,0 8px,8px -8px,-8px 0',
          }}
        >
          {showPreview ? (
            <Box
              component="img"
              src={previewUrl}
              alt={label}
              onError={() => setLoadFailed(true)}
              sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            />
          ) : value ? (
            <Tooltip title={problem || 'Image unavailable'}>
              <BrokenImage color="error" />
            </Tooltip>
          ) : (
            <ImageIcon color="disabled" />
          )}
        </Box>

        <Box flex={1} minWidth={220}>
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            hidden
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              variant="outlined"
              size="small"
              startIcon={busy === 'upload' ? <CircularProgress size={16} /> : value ? <SwapHoriz /> : <CloudUpload />}
              disabled={!!busy}
              onClick={() => inputRef.current?.click()}
            >
              {busy === 'upload' ? 'Uploading...' : value ? 'Replace' : 'Upload'}
            </Button>
            {value && (
              <Button
                variant="outlined"
                size="small"
                color="error"
                startIcon={busy === 'remove' ? <CircularProgress size={16} /> : <DeleteOutline />}
                disabled={!!busy}
                onClick={() => setConfirmRemove(true)}
              >
                Remove
              </Button>
            )}
          </Stack>

          {value && info && !info.problem ? (
            <Box mt={1}>
              <Typography variant="caption" display="block" sx={{ wordBreak: 'break-all' }}>
                <strong>{info.fileName}</strong>
              </Typography>
              <Stack direction="row" spacing={0.5} mt={0.5} flexWrap="wrap" useFlexGap>
                {info.format && <Chip size="small" label={info.format.toUpperCase()} />}
                {info.width && info.height ? <Chip size="small" label={`${info.width}×${info.height} px`} /> : null}
                {info.bytes ? <Chip size="small" label={formatBytes(info.bytes)} /> : null}
                <Chip
                  size="small"
                  color={info.storage === 's3' ? 'primary' : 'default'}
                  label={`Stored on ${storageLabel(info.storage)}`}
                />
              </Stack>
            </Box>
          ) : value && !info && !problem ? (
            <Typography variant="caption" color="text.secondary" display="block" mt={1} sx={{ wordBreak: 'break-all' }}>
              {value}
            </Typography>
          ) : null}

          {rules && (
            <Typography variant="caption" color="text.secondary" display="block" mt={1}>
              Accepted: {rulesSummary(rules)}
              {rules.recommended ? ` — recommended ${rules.recommended}` : ''}
            </Typography>
          )}
          {storageMode && (
            <Typography variant="caption" color="text.secondary" display="block">
              New uploads go to {storageMode === 's3' ? 'Amazon S3' : 'local server storage'}.
            </Typography>
          )}
        </Box>
      </Box>

      {problem && !error && (
        <Alert severity="warning" sx={{ mt: 1, py: 0 }}>{problem}</Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mt: 1, py: 0 }} onClose={() => setError('')}>{error}</Alert>
      )}
      {notice && !error && (
        <Alert severity="success" sx={{ mt: 1, py: 0 }} onClose={() => setNotice('')}>{notice}</Alert>
      )}

      <Dialog open={confirmRemove} onClose={() => setConfirmRemove(false)}>
        <DialogTitle>Remove {label.toLowerCase()}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The file is deleted from storage and every app falls back to the default {label.toLowerCase()} immediately.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRemove(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleRemove}>Remove</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
